import { LEAD_STATUSES } from '@user/components/Leads/LeadCard/typings'
import { ILeadDetail, setAssigneeDate } from '@user/store/lead'
import type { IOpportunityDetail } from '@user/store/opportunity'
import { ENTITY_TYPES } from '@sales-center/common'
import { OPPORTUNITY_STATUSES } from '@user/components/Opportunities/OpportunityCard/typings'
import { useEffect, useMemo } from 'react'
import { useSelectCustomFieldData } from '@user/store/customFields'
import { useDispatch, useSelector } from 'react-redux'
import { selectAllUsers } from '@user/store/users'

interface Props {
  detail: ILeadDetail | IOpportunityDetail
  entityId: number
  entityType: ENTITY_TYPES
  leadStatus?: LEAD_STATUSES
  opportunityStatus?: OPPORTUNITY_STATUSES
  canUpdate: boolean
  salesTeamId: string
  entityMatched: boolean
}

export function useData(props: Props) {
  const { detail } = props

  const { customFields, customFieldOptions } = useSelectCustomFieldData()
  const allUsers = useSelector(selectAllUsers)
  const dispatch = useDispatch()

  const isDisqualifiedLead = props.leadStatus === LEAD_STATUSES.Disqualified

  const targetPestList = useMemo(() => {
    if (
      !!detail.targetPests &&
      Array.isArray(detail.targetPests) &&
      detail.targetPests.length > 0
    ) {
      return detail.targetPests.join(', ')
    } else {
      return ''
    }
  }, [detail.targetPests])

  const showEditButton =
    !isDisqualifiedLead &&
    props.canUpdate &&
    (!props.opportunityStatus ||
      props.opportunityStatus == OPPORTUNITY_STATUSES.Open)

  const ownerTitle = useMemo(() => {
    if (props?.detail?.assignee?.fullName) {
      if (props?.detail?.assignee?.isActive === false) {
        return `${props?.detail?.assignee?.fullName} (Inactive)`
      }
      return props?.detail?.assignee?.fullName
    }
    return 'Unassigned'
  }, [props?.detail?.assignee])

  const customFieldValues = useMemo(() => {
    return customFields.map((c) => {
      const val = detail.customFieldValues?.find(
        (x) => x.customFieldId === c.id,
      )

      return {
        id: c.id,
        customFieldId: c.id,
        fieldName: c.name,
        customFieldOptionId: null,
        customFieldValueOptions: null,
        bool: null,
        date: null,
        number: null,
        text: null,
        type: c.type,
        ...val,
      }
    })
  }, [customFields, detail.customFieldValues])

  useEffect(() => {
    if (detail.assigneeId) {
      const assignee = allUsers.find((x) => x.id === detail.assigneeId)

      if (assignee) {
        dispatch(
          setAssigneeDate({
            leadId: detail.id,
            assignee: { ...assignee, fullName: assignee.fullName || '' },
          }),
        )
      }
    }
  }, [allUsers, detail.assigneeId, detail.id, dispatch])

  return {
    customFieldValues,
    targetPestList,
    showEditButton,
    ownerTitle,
    customFields,
    customFieldOptions,
  }
}
